<script setup lang="ts">
import { computed, ref, type VNodeChild } from 'vue'
import { Info, CheckCircle2, AlertTriangle, CircleX, X } from '@lucide/vue'
import RenderContent from '../internal/RenderContent'

const props = withDefaults(defineProps<{
  message?: VNodeChild
  description?: VNodeChild
  type?: 'success' | 'info' | 'warning' | 'error'
  showIcon?: boolean
  closable?: boolean
  closeText?: VNodeChild
  banner?: boolean
  afterClose?: () => void
}>(), { showIcon: undefined, closable: false, banner: false })
const emit = defineEmits<{ close: [event: MouseEvent] }>()
const closed = ref(false)
const type = computed(() => props.type ?? (props.banner ? 'warning' : 'info'))
const icon = computed(() => ({ success: CheckCircle2, info: Info, warning: AlertTriangle, error: CircleX })[type.value])
function close(event: MouseEvent) { emit('close', event); closed.value = true }
</script>

<template>
  <Transition name="rr-alert" @after-leave="afterClose?.()"><div v-if="!closed" class="rr-alert" :class="[`rr-alert--${type}`, { 'is-banner': banner, 'has-description': description || $slots.description }]" :role="type === 'error' || type === 'warning' ? 'alert' : 'status'">
    <span v-if="showIcon ?? banner" class="rr-alert__icon"><slot name="icon"><component :is="icon" :size="description || $slots.description ? 22 : 16" aria-hidden="true" /></slot></span>
    <div class="rr-alert__content"><div v-if="message || $slots.message" class="rr-alert__message"><slot name="message"><RenderContent :value="message" /></slot></div><div v-if="description || $slots.description" class="rr-alert__description"><slot name="description"><RenderContent :value="description" /></slot></div></div>
    <div v-if="$slots.action" class="rr-alert__action"><slot name="action" /></div>
    <button v-if="closable || closeText || $slots.closeText" type="button" class="rr-alert__close" aria-label="关闭提示" @click="close"><slot name="closeText"><RenderContent v-if="closeText" :value="closeText" /><X v-else :size="14" aria-hidden="true" /></slot></button>
  </div></Transition>
</template>

<style scoped>
.rr-alert { display: flex; align-items: flex-start; gap: var(--rr-space-2); padding: var(--rr-space-2) var(--rr-space-3); border: 1px solid var(--rr-alert-border); border-radius: var(--rr-radius-control); background: var(--rr-alert-bg); color: var(--rr-color-text); font-size: var(--rr-font-size-base); line-height: 1.6; }
.rr-alert--success { --rr-alert-color: var(--rr-color-success); --rr-alert-border: var(--rr-color-success-border); --rr-alert-bg: var(--rr-color-success-soft); }
.rr-alert--info { --rr-alert-color: var(--rr-color-info); --rr-alert-border: var(--rr-color-info-border); --rr-alert-bg: var(--rr-color-info-soft); }
.rr-alert--warning { --rr-alert-color: var(--rr-color-warning); --rr-alert-border: var(--rr-color-warning-border); --rr-alert-bg: var(--rr-color-warning-soft); }
.rr-alert--error { --rr-alert-color: var(--rr-color-danger); --rr-alert-border: var(--rr-color-danger-border); --rr-alert-bg: var(--rr-color-danger-soft); }
.rr-alert.is-banner { border: 0; border-radius: 0; }
.rr-alert__icon { display: inline-flex; color: var(--rr-alert-color); margin-top: 2px; }
.rr-alert__content { flex: 1; min-width: 0; overflow-wrap: anywhere; }
.rr-alert.has-description .rr-alert__message { font-weight: 500; }
.rr-alert__description { color: var(--rr-color-text-muted); }
.rr-alert__action { flex-shrink: 0; }
.rr-alert__close { display: inline-flex; padding: 2px; min-height: 0; border: 0; background: transparent; color: var(--rr-color-text-muted); cursor: pointer; }
.rr-alert-leave-active { transition: opacity 160ms ease, transform 160ms ease; }
.rr-alert-leave-to { opacity: 0; transform: translateY(-4px); }
@media (prefers-reduced-motion: reduce) { .rr-alert-leave-active { transition: none; } }
</style>
